/**
 * MKS-334 — Receivables, for admin and finance.
 *
 * A page of its own rather than a redesign of `/invoices`: the invoice list
 * answers "what did we bill", this answers "who owes us what, and for how
 * long". `InvoiceListPage` is untouched.
 *
 * Scope is Petra's "keep the whole view simple": one per-client table with the
 * aging buckets AS COLUMNS, a totals row, two filters, click-to-expand detail,
 * and the two DSO numbers in the header. No separate aging table, no chart.
 *
 * Everything the table shows is computed server-side (one request, rows
 * already sorted, each client's unpaid invoices nested so expanding costs no
 * second call). The page re-derives nothing — in particular it never recounts
 * "days past due" from a due date, which is the one definition
 * `invoiceDaysOverdue` owns.
 *
 * The one thing filtered in the browser is the CLIENT dropdown: the row set is
 * one row per client and therefore small, and narrowing it here keeps every
 * client in the dropdown instead of leaving a one-option list behind after a
 * selection. The endpoint takes `client=` with the same semantics for callers
 * that want the server to do it.
 */
import { Fragment, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useReceivables } from '../../hooks/useReceivables';
import type {
  ReceivablesAmounts,
  ReceivablesClientRow,
  ReceivablesDso,
} from '../../hooks/useReceivables';
import { Spinner } from '../../components/common/Spinner';
import { formatCurrency, formatDisplayDate } from '../../utils/format';
import { formatNumber } from '../../utils/localeFormat';
import { formatInvoiceDueDate } from '../../utils/invoiceDueDate';
import { useFlag } from '../../services/flagsContext';

/**
 * Printed on the page, verbatim, next to the numbers — so the figure and its
 * definition cannot drift apart.
 */
const DSO_FORMULA =
  'DSO = (outstanding receivables at the end of the window ÷ total invoiced in the window) × days in the window';

/** The two windows, as finance says them out loud. */
const DSO_WINDOW_LABELS: Record<number, string> = {
  90: '3 months',
  365: '12 months',
};

/** The aging columns, in the order they are read. */
const BUCKET_COLUMNS = [
  ['d1to30', '1–30'],
  ['d31to60', '31–60'],
  ['d61to90', '61–90'],
  ['d90plus', '90+'],
] as const;

const money = (value: number, currency: string) => formatCurrency(value, false, currency);

/**
 * MKS-454 — DSO in WHOLE days, grouped: `4 369 days`, not `4368.5 days`.
 *
 * Petra 2026-10-21: a tenth of a day is noise in a figure that is a ratio of
 * two money totals, and an ungrouped four-digit day count is unreadable. The
 * rounding is display-only — the server still sends the figure to one decimal,
 * and the receivables ÷ invoiced × days line under it is unchanged, so the
 * arithmetic stays checkable.
 */
function dsoDays(days: number, wholeDays: boolean): string {
  return wholeDays ? `${formatNumber(Math.round(days))} days` : `${days} days`;
}

function DsoFigure({
  dso,
  currency,
  wholeDays,
}: {
  dso: ReceivablesDso;
  currency: string;
  wholeDays: boolean;
}) {
  const label = DSO_WINDOW_LABELS[dso.windowDays] ?? `${dso.windowDays} days`;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label} ({dso.windowDays} days)
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
        {dso.days === null ? '–' : dsoDays(dso.days, wholeDays)}
      </div>
      <div className="mt-1 text-xs text-gray-500 tabular-nums">
        {dso.days === null ? (
          <>Nothing invoiced between {formatDisplayDate(dso.from)} and {formatDisplayDate(dso.to)}</>
        ) : (
          <>
            {money(dso.receivables, currency)} ÷ {money(dso.invoiced, currency)} × {dso.windowDays}
          </>
        )}
      </div>
    </div>
  );
}

/** The figures shared by a client row and the totals row, as `<td>`s. */
function AmountCells({ amounts, currency }: { amounts: ReceivablesAmounts; currency: string }) {
  return (
    <>
      <td className="px-3 py-2 text-right tabular-nums font-medium text-red-700">
        {money(amounts.overdueTotal, currency)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{amounts.overdueInvoiceCount}</td>
      <td className="px-3 py-2 text-right tabular-nums">
        {amounts.oldestDaysOverdue > 0 ? `${amounts.oldestDaysOverdue} d` : '–'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {money(amounts.notYetDueTotal, currency)}
      </td>
      {BUCKET_COLUMNS.map(([key]) => (
        <td key={key} className="px-3 py-2 text-right tabular-nums">
          {amounts.buckets[key] > 0 ? money(amounts.buckets[key], currency) : '–'}
        </td>
      ))}
    </>
  );
}

/** One client's unpaid invoices, shown in place under the client row. */
function InvoiceDetail({ row }: { row: ReceivablesClientRow }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="px-3 py-1 font-medium">Invoice</th>
          <th className="px-3 py-1 font-medium">Project</th>
          <th className="px-3 py-1 font-medium">Issued</th>
          <th className="px-3 py-1 font-medium">Due</th>
          <th className="px-3 py-1 font-medium text-right">Days past due</th>
          <th className="px-3 py-1 font-medium text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {row.invoices.map((invoice) => (
          <tr key={invoice.id} className="border-t border-gray-100">
            <td className="px-3 py-1.5">
              <Link to={`/invoices/${invoice.id}`} className="text-primary-600 hover:underline">
                {invoice.invoiceNumber || invoice.name || `Invoice #${invoice.id}`}
              </Link>
            </td>
            <td className="px-3 py-1.5 text-gray-700">{invoice.projectName || '–'}</td>
            <td className="px-3 py-1.5 text-gray-700">{formatDisplayDate(invoice.issueDate)}</td>
            {/* MKS-454 — a derived due date is shown WITH its term, so the
                "280 d" beside it is explained and nobody reads the date as
                one accounting gave us. "no due date" is the flag-off text. */}
            <td className="px-3 py-1.5 text-gray-700">
              {formatInvoiceDueDate(invoice, 'no due date')}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums">
              {invoice.daysOverdue > 0 ? `${invoice.daysOverdue} d` : '–'}
            </td>
            <td className="px-3 py-1.5 text-right tabular-nums">
              {money(invoice.amount, row.currency)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ReceivablesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(null);
  // MKS-454 — the same flag the server derives due dates behind, so the page
  // never shows whole-day DSO over figures that are still bucketed the old
  // way (and the marker in the expanded rows appears with it).
  const wholeDayDso = useFlag('mks_454_derived_invoice_due_date');

  const clientFilter = searchParams.get('client') || '';
  const overdueOnly = searchParams.get('overdueOnly') !== 'false';

  const setParam = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params, { replace: true });
  };

  const { data, isLoading, error } = useReceivables({ overdueOnly });

  // Every client the server returned, for the dropdown — computed before the
  // client filter narrows the table, so selecting one never empties the list.
  const clientOptions = useMemo(
    () => (data?.rows ?? []).map((row) => ({ key: row.key, name: row.customerName })),
    [data],
  );

  const rows = useMemo(
    () => (data?.rows ?? []).filter((row) => (clientFilter ? row.key === clientFilter : true)),
    [data, clientFilter],
  );

  // The totals row has to equal the sum of the rows ABOVE it, so a client
  // filter re-sums what is shown rather than keeping the server's whole-table
  // totals. With no filter the two are the same figures.
  const totals: ReceivablesAmounts | null = useMemo(() => {
    if (!data) return null;
    if (!clientFilter) return data.totals;
    return rows.reduce<ReceivablesAmounts>(
      (total, row) => ({
        outstandingTotal: total.outstandingTotal + row.outstandingTotal,
        overdueTotal: total.overdueTotal + row.overdueTotal,
        notYetDueTotal: total.notYetDueTotal + row.notYetDueTotal,
        overdueInvoiceCount: total.overdueInvoiceCount + row.overdueInvoiceCount,
        invoiceCount: total.invoiceCount + row.invoiceCount,
        oldestDaysOverdue: Math.max(total.oldestDaysOverdue, row.oldestDaysOverdue),
        buckets: {
          d1to30: total.buckets.d1to30 + row.buckets.d1to30,
          d31to60: total.buckets.d31to60 + row.buckets.d31to60,
          d61to90: total.buckets.d61to90 + row.buckets.d61to90,
          d90plus: total.buckets.d90plus + row.buckets.d90plus,
        },
      }),
      {
        outstandingTotal: 0,
        overdueTotal: 0,
        notYetDueTotal: 0,
        overdueInvoiceCount: 0,
        invoiceCount: 0,
        oldestDaysOverdue: 0,
        buckets: { d1to30: 0, d31to60: 0, d61to90: 0, d90plus: 0 },
      },
    );
  }, [data, rows, clientFilter]);

  if (isLoading) {
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );
  }

  if (error || !data || !totals) {
    return <div className="p-6 text-sm text-red-700">Could not load receivables.</div>;
  }

  const currency = data.currency;

  return (
    <div className="p-6 space-y-6">
      <header className="space-y-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Receivables</h1>
          <p className="text-sm text-gray-500">
            Unpaid client invoices as of {formatDisplayDate(data.asOf)}.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.dso.map((dso) => (
            <DsoFigure
              key={dso.windowDays}
              dso={dso}
              currency={currency}
              wholeDays={wholeDayDso}
            />
          ))}
          <p className="self-center text-xs leading-relaxed text-gray-500 lg:col-span-1 sm:col-span-2">
            {DSO_FORMULA}
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="block text-xs font-medium text-gray-600">Client</span>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={clientFilter}
            onChange={(e) => setParam('client', e.target.value)}
          >
            <option value="">All clients</option>
            {clientOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs font-medium text-gray-600">Show</span>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={overdueOnly ? 'overdue' : 'all'}
            onChange={(e) => setParam('overdueOnly', e.target.value === 'all' ? 'false' : '')}
          >
            <option value="overdue">Overdue only</option>
            <option value="all">All unpaid</option>
          </select>
        </label>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">
          {overdueOnly ? 'No client is overdue.' : 'No unpaid invoices.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Client</th>
                <th className="px-3 py-2 text-right font-medium">Overdue</th>
                <th className="px-3 py-2 text-right font-medium">Overdue invoices</th>
                <th className="px-3 py-2 text-right font-medium">Oldest past due</th>
                <th className="px-3 py-2 text-right font-medium">Not yet due</th>
                {BUCKET_COLUMNS.map(([key, label]) => (
                  <th key={key} className="px-3 py-2 text-right font-medium">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <Fragment key={row.key}>
                  <tr
                    // The whole row is the control; the button inside is the
                    // keyboard and screen-reader affordance, and its click
                    // bubbles here.
                    onClick={() => setExpanded(expanded === row.key ? null : row.key)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        aria-expanded={expanded === row.key}
                        className="text-left font-medium text-gray-900 hover:underline"
                      >
                        {row.customerName}
                      </button>
                      <span className="ml-2 text-xs text-gray-500">{row.invoiceCount} unpaid</span>
                    </td>
                    <AmountCells amounts={row} currency={currency} />
                  </tr>
                  {expanded === row.key && (
                    <tr>
                      <td colSpan={5 + BUCKET_COLUMNS.length} className="bg-gray-50 px-3 py-3">
                        <InvoiceDetail row={row} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
              <tr>
                <td className="px-3 py-2">Total</td>
                <AmountCells amounts={totals} currency={currency} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
